/**
 * Point d'entrée de la SPA (ARCHITECTURE.md §3).
 *
 * TanStack Query porte tout l'état serveur (§6.1). Aucune bibliothèque d'état
 * global ne doit être ajoutée : `useState` / `useReducer` suffisent pour le
 * reste.
 */

import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { isUnauthenticated } from './api/client';
import App from './App';
import { startPersistence } from './lib/persist';
import { registerServiceWorker } from './lib/push';
import { onSyncDrained, startSyncQueue, watchSyncTriggers } from './lib/syncQueue';
import { applyTheme, storedChoice } from './lib/theme';
/*
 * ⚠️ **L'ordre de ces cinq imports compte.**
 *
 * `tokens-nourri.css` vient en premier : c'est **la** palette de
 * l'application — l'ancien `tokens.css` et le pont d'alias qui le reliait à
 * celle-ci ont été supprimés le 26/08/2026, quand la dernière surface a été
 * migrée. Les autres feuilles ne font que la consommer, et CSS n'a pas de
 * résolution de dépendances : une variable lue avant d'être définie vaut la
 * chaîne vide, et la règle qui l'emploie est simplement ignorée.
 *
 * ⚠️ `surfaces.css` s'appelle ainsi depuis la refonte du 25/08/2026 : le
 * matériau Liquid Glass a été déposé, et un fichier qui ne porte plus de verre
 * ne doit pas continuer à s'appeler `glass.css`.
 *
 * ⚠️ Les deux derniers viennent en **dernier**, et dans cet ordre :
 * `nourri.css` porte les six composants de la bibliothèque,
 * `nourri-screens.css` l'assemblage des écrans qui les emploie. Le second cadre
 * le premier (marges de page, grilles, rangs de titre) et redresse quelques
 * règles de `surfaces.css` / `controls.css` : à spécificité égale, c'est
 * l'ordre source qui tranche.
 */
import './styles/tokens-nourri.css';
import './styles/surfaces.css';
import './styles/controls.css';
import './styles/nourri.css';
import './styles/nourri-screens.css';

/**
 * Une session peut expirer ou être révoquée pendant que l'app est ouverte —
 * notamment après une reprise par clé de secours, qui ferme toutes les autres
 * sessions. Plutôt que de laisser chaque écran gérer son 401, on relit l'état
 * d'authentification : la garde (`AuthGate`) bascule alors sur l'écran de
 * connexion.
 */
function onApiError(error: unknown): void {
  if (isUnauthenticated(error)) {
    void queryClient.invalidateQueries({ queryKey: ['auth', 'status'] });
  }
}

/**
 * Racines de cache à relire après un drainage (V2 §3.3).
 *
 * 🔴 **Il faut relire, et pas seulement pour faire propre.** Hors ligne, tout ce
 * que l'utilisateur a vu n'était qu'un **aperçu optimiste** : des `id` négatifs,
 * des macros calculées par le client, des sous-totaux approchés. Ce qui fait
 * foi, c'est ce que le serveur a écrit (§5.1) — et c'est seulement au drainage
 * qu'on peut le lui demander.
 *
 * ⚠️ **Une liste, pas un `invalidateQueries()` nu.** Tout invalider emporterait
 * `food-search`, dont la moitié Open Food Facts part chez un tiers (§5.2) :
 * revenir en ligne déclencherait une rafale de requêtes vers OFF pour des
 * recherches que personne ne regarde plus. La liste est la même que celle des
 * racines persistées, moins `auth` — l'état d'authentification vient d'être
 * exercé par le drainage lui-même.
 */
const RACINES_APRES_DRAINAGE = [
  'day',
  'templates',
  'planning',
  'quick-access',
  'foods',
  'food',
  'recipes',
  'recipe-tags',
  // V2 étape 4 — la réplique et les détails consultés. Un exercice créé hors
  // ligne n'existe dans la liste que sous sa forme d'aperçu tant que le
  // serveur ne l'a pas écrit ; c'est au drainage qu'on relit ce qui fait foi.
  'exercises',
  'exercise',
  'weight',
  'weight-goals',
  'notifications',
] as const;

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onApiError }),
  mutationCache: new MutationCache({ onError: onApiError }),
  defaultOptions: {
    queries: {
      // Application mono-utilisateur : les données ne changent que par nos
      // propres mutations. Inutile de refetcher au moindre retour d'onglet.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: 1,
      /*
       * 🔴 **Sept jours, et c'est ce qui rend la persistance utile**
       * (`ARCHITECTURE_V2.md` §3.2, étape 2 de la V2).
       *
       * Le défaut de TanStack Query est de **cinq minutes** : une requête sans
       * observateur est collectée passé ce délai, donc absente du cache au
       * moment où on le persiste. Avec ce défaut, la journée consultée il y a
       * dix minutes ne serait pas persistée — et la fenêtre J−7 → J+1 promise
       * par le §3.1 serait vide en pratique.
       *
       * ⚠️ Le volume reste trivial : une journée pèse quelques kilo-octets, et
       * `lib/persist.ts` borne ce qui part sur le disque à la fenêtre du §3.1.
       * Ce délai-ci ne concerne que le cache **mémoire**, qui disparaît de
       * toute façon au rechargement de la page.
       */
      gcTime: 7 * 24 * 60 * 60 * 1000,
    },
    mutations: {
      /*
       * 🔴 **`'always'`, et sans cela toute l'étape 3 serait inopérante.**
       *
       * Le défaut de TanStack Query v5 est `networkMode: 'online'` : quand
       * `navigator.onLine` est **faux**, `mutationFn` n'est **jamais appelée**.
       * La mutation est mise en pause en mémoire et rejouée au retour du
       * réseau. C'est la découverte du banc de l'étape 1, et c'est ce qui
       * expliquait que le hors ligne « marchait un peu » avant qu'une file
       * n'existe.
       *
       * Conséquence si on n'y touche pas : en **mode avion**, `apiFetch` n'est
       * jamais atteint, donc rien n'entre jamais dans la file — et la pause,
       * elle, ne survit ni à la fermeture de l'application ni au redémarrage de
       * l'appareil (§3.3). On perdrait exactement ce que l'étape existe pour
       * garantir, et le banc du mode avion passerait quand même : la saisie
       * repart bien au retour du réseau **tant qu'on n'a pas fermé l'onglet**.
       *
       * Avec `'always'`, `mutationFn` est appelée quoi qu'il arrive, `apiFetch`
       * voit la requête, et la file la couche en IndexedDB.
       *
       * ⚠️ **Les requêtes, elles, gardent le défaut.** Une lecture hors ligne
       * doit rester en pause et laisser le cache persisté de l'étape 2
       * s'afficher ; la passer en `'always'` la ferait échouer bruyamment pour
       * ne rien apporter.
       */
      networkMode: 'always',
      /*
       * ⚠️ **Aucun réessai ici : la file en a la charge** (§3.3, temporisation
       * croissante 1 s → 60 s). Deux mécanismes de réessai superposés
       * émettraient la même opération deux fois — inoffensif grâce à
       * l'idempotence, mais impossible à lire dans un journal.
       */
      retry: 0,
    },
  },
});

/*
 * Service worker (étape 9, §6.4).
 *
 * ⚠️ **Enregistrer n'est pas demander.** Poser le service worker au démarrage
 * ne déclenche aucune permission et n'affiche rien : c'est silencieux. La règle
 * du §6.4 — « la demande de permission doit suivre un geste utilisateur
 * explicite dans les Réglages, jamais au démarrage » — porte sur
 * `Notification.requestPermission()`, appelé uniquement depuis
 * `screens/settings/NotificationsSection.tsx`.
 *
 * Le faire ici plutôt qu'au moment du geste est même nécessaire : le service
 * worker doit être **actif** pour qu'on puisse s'abonner, et l'attendre pendant
 * le tap allongerait la fenêtre où Safari iOS perd le contexte du geste.
 *
 * ⚠️ **Depuis la V2 (étape 2), ce service worker met la coquille en cache** —
 * `index.html`, `/assets/*`, manifeste et icônes (§3.2). Il ne met en revanche
 * **jamais** de réponse `/api/*` en cache : le cache des données appartient à
 * TanStack Query, qu'on persiste juste en dessous.
 *
 * Un échec d'enregistrement reste sans conséquence sur l'application en ligne :
 * on perd les notifications et le hors ligne, rien n'est bloqué ici.
 */
void registerServiceWorker();

/*
 * Thème (étape 10, specs §6.8 et §8.1).
 *
 * ⚠️ **Ce n'est pas la première application du thème** — le script inline
 * d'`index.html` a déjà posé `data-theme` avant le premier pixel, et c'est lui
 * qui évite le clignotement. Ce passage-ci a un autre rôle : poser
 * `<meta name="theme-color">`, que le script inline ne peut pas remplir parce
 * que la valeur vit dans `tokens-nourri.css`, qui n'est pas encore chargé à
 * ce moment-là.
 */
applyTheme(storedChoice());

/*
 * Cache hors ligne (V2, étape 2 — `ARCHITECTURE_V2.md` §3.2).
 *
 * 🔴 **On attend la restauration avant de rendre, et c'est délibéré.** Rendre
 * d'abord ferait démarrer l'application sur un cache vide : hors ligne, chaque
 * écran partirait en requête, échouerait, afficherait son erreur, puis serait
 * remplacé par les données restaurées quelques dizaines de millisecondes plus
 * tard. L'utilisateur verrait donc l'écran cassé **avant** l'écran correct —
 * l'inverse exact de ce que le hors ligne promet.
 *
 * ⚠️ `startPersistence` ne lève jamais et porte son propre plafond d'attente :
 * un stockage indisponible (navigation privée, quota, base verrouillée par un
 * autre onglet) dégrade le hors ligne, il n'empêche pas l'application de
 * s'ouvrir.
 *
 * ⚠️ **Ceci ne persiste que des lectures.** Les mutations en attente restent en
 * mémoire seulement — c'est le sujet de la file de l'étape 3, pas de celle-ci.
 */
await startPersistence(queryClient);

/*
 * File de synchronisation (V2, étape 3 — `ARCHITECTURE_V2.md` §3.3).
 *
 * 🔴 **Restaurée avant le rendu, comme le cache de lecture.** Un écran monté
 * avant que la file ne soit relue verrait `hasQueuedWork()` répondre « non » et
 * enverrait sa mutation **directement** — donc devant des opérations plus
 * anciennes qui attendent en IndexedDB. L'ordre d'émission est la clé de tout
 * le §3.3 ; il se perdrait dès la première saisie suivant un redémarrage.
 *
 * ⚠️ `startSyncQueue` ne lève jamais, pour la même raison que
 * `startPersistence` : un stockage indisponible dégrade le hors ligne, il
 * n'empêche pas l'application de s'ouvrir.
 */
await startSyncQueue();

/*
 * ⚠️ **Le drainage n'est pas déclenché ici.** Il l'est par `watchSyncTriggers`
 * (événement `online`, retour au premier plan) et par le premier succès de
 * requête (`noteNetworkAlive`). Le faire aussi au démarrage doublerait le
 * déclencheur sans rien ajouter : la garde `AuthGate` interroge
 * `/api/auth/status` au montage, et ce succès-là réveille déjà la file.
 */
watchSyncTriggers();

onSyncDrained(() => {
  for (const racine of RACINES_APRES_DRAINAGE) {
    void queryClient.invalidateQueries({ queryKey: [racine] });
  }
});

const container = document.getElementById('root');
if (!container) {
  throw new Error('#root introuvable dans index.html');
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
